import React from 'react';
import styled from 'styled-components';
import ID from 'react-form-builder2/lib/UUID';
import IntlMessages from 'react-form-builder2/lib/language-provider/IntlMessages';
import { PlusIcon, MinusIcon } from '../shared/Icons';
import { ADMIN_COLORS } from '../../theme';

/* Matches AddressElementsEditor's palette, so the row-action buttons read as
   the same drawer rather than the library's raw Bootstrap green/red pair. */
const COLORS = {
  border: ADMIN_COLORS.border,
  faint: ADMIN_COLORS.textFaint,
  accent: ADMIN_COLORS.accent,
  accentHover: ADMIN_COLORS.accentHover,
  danger: ADMIN_COLORS.danger,
  dangerHover: '#b91c1c',
  hoverBg: ADMIN_COLORS.hover,
  surface: ADMIN_COLORS.surface,
};

const RowActionButton = styled.button<{ $variant: 'add' | 'remove' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;

  border: 1px solid ${COLORS.border} !important;
  border-radius: 6px;
  background: ${COLORS.surface};
  color: ${(p) => (p.$variant === 'remove' ? COLORS.danger : COLORS.accent)};
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${COLORS.hoverBg};
    border-color: ${(p) =>
      p.$variant === 'remove' ? COLORS.dangerHover : COLORS.accentHover} !important;
    color: ${(p) => (p.$variant === 'remove' ? COLORS.dangerHover : COLORS.accentHover)};
  }

  & + & {
    margin-left: 6px;
  }
`;

interface OptionItem {
  value: string;
  text: string;
  key: string;
  correct?: boolean;
}

interface DynamicOptionListProps {
  element: any;
  data?: any;
  preview?: any;
  showCorrectColumn?: boolean;
  canHaveOptionCorrect?: boolean;
  canHaveOptionValue?: boolean;
  updateElement?: (element: any) => void;
}

interface DynamicOptionListState {
  element: any;
  data: any;
  dirty: boolean;
}

export default class DynamicOptionList extends React.Component<
  DynamicOptionListProps,
  DynamicOptionListState
> {
  constructor(props: DynamicOptionListProps) {
    super(props);
    this.state = {
      element: this.props.element,
      data: this.props.data,
      dirty: false,
    };
  }

  _setValue(text: string): string {
    return text.replace(/[^A-Z0-9]+/gi, '_').toLowerCase();
  }

  editOption(optionIndex: number, e: React.ChangeEvent<HTMLInputElement>): void {
    const thisElement = this.state.element;
    const val =
      thisElement.options[optionIndex].value !==
      this._setValue(thisElement.options[optionIndex].text)
        ? thisElement.options[optionIndex].value
        : this._setValue(e.target.value);
    thisElement.options[optionIndex].text = e.target.value;
    thisElement.options[optionIndex].value = val;
    this.setState({ element: thisElement, dirty: true });
  }

  editValue(optionIndex: number, e: React.ChangeEvent<HTMLInputElement>): void {
    const thisElement = this.state.element;
    const val =
      e.target.value === ''
        ? this._setValue(thisElement.options[optionIndex].text)
        : e.target.value;
    thisElement.options[optionIndex].value = val;
    this.setState({ element: thisElement, dirty: true });
  }

  editOptionCorrect(optionIndex: number): void {
    const thisElement = this.state.element;
    if (
      Object.prototype.hasOwnProperty.call(
        thisElement.options[optionIndex],
        'correct',
      )
    ) {
      delete thisElement.options[optionIndex].correct;
    } else {
      thisElement.options[optionIndex].correct = true;
    }
    this.setState({ element: thisElement });
    this.props.updateElement?.call(this.props.preview, thisElement);
  }

  updateOption(): void {
    const thisElement = this.state.element;
    // to prevent ajax calls with no change
    if (this.state.dirty) {
      this.props.updateElement?.call(this.props.preview, thisElement);
      this.setState({ dirty: false });
    }
  }

  addOption(index: number): void {
    const thisElement = this.state.element;
    thisElement.options.splice(index + 1, 0, {
      value: '',
      text: '',
      key: ID.uuid(),
    });
    this.props.updateElement?.call(this.props.preview, thisElement);
  }

  removeOption(index: number): void {
    const thisElement = this.state.element;
    thisElement.options.splice(index, 1);
    this.props.updateElement?.call(this.props.preview, thisElement);
  }

  render(): JSX.Element {
    if (this.state.dirty) {
      this.state.element.dirty = true;
    }

    const { canHaveOptionValue, canHaveOptionCorrect } = this.props;

    return (
      <div className="dynamic-option-list">
        <ul>
          <li>
            <div className="row">
              <div className="col-sm-6">
                <b>
                  <IntlMessages id="options" />
                </b>
              </div>
              {canHaveOptionValue && (
                <div className="col-sm-2">
                  <b>
                    <IntlMessages id="value" />
                  </b>
                </div>
              )}
              {canHaveOptionValue && canHaveOptionCorrect && (
                <div className="col-sm-4">
                  <b>
                    <IntlMessages id="correct" />
                  </b>
                </div>
              )}
              <div className="col-sm-3" />
            </div>
          </li>
          {this.state.element.options.map(
            (option: OptionItem, index: number) => {
              const thisKey = `edit_${option.key}`;
              const val =
                option.value !== this._setValue(option.text)
                  ? option.value
                  : '';
              return (
                <li className="clearfix" key={thisKey}>
                  <div className="row">
                    <div className="col-sm-6">
                      <input
                        tabIndex={index + 1}
                        className="form-control"
                        style={{ width: '100%' }}
                        type="text"
                        name={`text_${index}`}
                        placeholder="Option text"
                        value={option.text}
                        onBlur={this.updateOption.bind(this)}
                        onChange={this.editOption.bind(this, index)}
                      />
                    </div>
                    {canHaveOptionValue && (
                      <div className="col-sm-2">
                        <input
                          className="form-control"
                          type="text"
                          name={`value_${index}`}
                          placeholder="value"
                          value={val}
                          onBlur={this.updateOption.bind(this)}
                          onChange={this.editValue.bind(this, index)}
                        />
                      </div>
                    )}
                    {canHaveOptionValue && canHaveOptionCorrect && (
                      <div className="col-sm-1">
                        <input
                          className="form-control"
                          type="checkbox"
                          value="1"
                          onChange={this.editOptionCorrect.bind(this, index)}
                          checked={Object.prototype.hasOwnProperty.call(
                            option,
                            'correct',
                          )}
                        />
                      </div>
                    )}
                    <div className="col-sm-3">
                      <div
                        className="dynamic-options-actions-buttons"
                        style={{ display: 'flex' }}
                      >
                        <RowActionButton
                          type="button"
                          $variant="add"
                          onClick={this.addOption.bind(this, index)}
                          title="Add option below"
                          aria-label="Add option below"
                        >
                          <PlusIcon size={14} />
                        </RowActionButton>
                        {index > 0 && (
                          <RowActionButton
                            type="button"
                            $variant="remove"
                            onClick={this.removeOption.bind(this, index)}
                            title="Remove option"
                            aria-label="Remove option"
                          >
                            <MinusIcon size={14} />
                          </RowActionButton>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            },
          )}
        </ul>
      </div>
    );
  }
}
